import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { BrandMark } from "@/components/BrandMark";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { fetchSiteContent } from "@/lib/lumenApi";

const libraryCopy = {
  law: [
    "La Bibliothèque ordonne les profondeurs du sanctuaire.",
    "Elle n’ajoute ni chambres ni inventaire.",
  ],
  cartography: "Les territoires donnent au lieu son ordre visible.",
  depths: "Plus loin, figures, Atlas et pôles incarnés demeurent en réserve.",
  traversal: "Ici, l’ordre précède la consultation.",
};

const depthZones = [
  { id: "figures", title: "Figures tutélaires", items: [] },
  { id: "atlas", title: "Atlas", items: ["Frise", "Civilisations", "Cycles de guerre"] },
  { id: "poles", title: "Pôles incarnés", items: ["Corps", "Énergie", "Aliments bénéfiques"] },
];

const figureConstellation = [
  {
    id: "marc-aurele",
    name: "Marc Aurèle",
    territory: "Sagesse",
    lineage: "Souveraineté intérieure",
    role: "Centre souverain",
    lessons: [
      { slug: "ce-qui-depend-de-toi", title: "Ce qui dépend de toi" },
      { slug: "la-mesure-devant-la-finitude", title: "La mesure devant la finitude" },
    ],
    variant: "center",
  },
  {
    id: "epictete",
    name: "Épictète",
    territory: "Discipline",
    lineage: "Maîtrise du discernement",
    role: "Pôle de rigueur",
    lessons: [
      { slug: "discipline-assentiment", title: "La discipline de l’assentiment" },
      { slug: "ce-qui-depend-de-toi", title: "Ce qui dépend de toi" },
    ],
    variant: "rigor",
  },
  {
    id: "sun-tzu",
    name: "Sun Tzu",
    territory: "Stratégie",
    lineage: "Art du rapport de force",
    role: "Pôle stratégique",
    lessons: [
      { slug: "terrain-avant-affrontement", title: "Le terrain avant l’affrontement" },
      { slug: "fortune-et-prise", title: "La fortune et la prise" },
    ],
    variant: "strategy",
  },
];

function LoadingState() {
  return (
    <div className="lumen-page library-page">
      <div className="lumen-shell flex min-h-screen items-center justify-center px-6 py-24">
        <div className="lumen-frame max-w-xl p-10 text-center">
          <p className="lumen-kicker" data-testid="library-loading-kicker">
            LUMEN
          </p>
          <h1 className="lumen-title mt-4 text-4xl text-[var(--lumen-ivory)]" data-testid="library-loading-title">
            La Bibliothèque prend forme.
          </h1>
          <p className="lumen-copy mt-4" data-testid="library-loading-copy">
            Quelques instants suffisent pour laisser apparaître le lieu.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function LibraryPage() {
  const { user } = useAuth();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let active = true;

    fetchSiteContent()
      .then((data) => {
        if (active) {
          setSite(data);
        }
      })
      .catch(() => {
        if (active) {
          setErrorMessage("La Bibliothèque reste silencieuse pour l’instant. Réessaie dans un moment.");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [user]);

  if (loading || !site) {
    if (!loading && errorMessage) {
      return (
        <div className="lumen-page library-page">
          <div className="lumen-shell flex min-h-screen items-center justify-center px-6 py-24">
            <div className="lumen-frame max-w-xl p-10 text-center">
              <p className="lumen-kicker" data-testid="library-error-kicker">
                LUMEN
              </p>
              <h1 className="lumen-title mt-4 text-4xl text-[var(--lumen-ivory)]" data-testid="library-error-title">
                Le lieu n’est pas encore visible.
              </h1>
              <p className="lumen-copy mt-4" data-testid="library-error-copy">
                {errorMessage}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return <LoadingState />;
  }

  const firstLesson = site.lessons.find((lesson) => lesson.slug === "effet-miroir") ?? site.lessons[0];
  const entitlements = user && user !== false ? user.entitlements || { cycle: false, integral: false } : { cycle: false, integral: false };
  const traversalTitle = entitlements.integral
    ? "Le corpus vivant est ouvert."
    : entitlements.cycle
      ? "Le premier cycle est ouvert."
      : "Le dedans du corpus demeure derrière le seuil.";
  const traversalCopy = entitlements.integral
    ? "Cycle I • Strate II • Bibliothèque approfondie."
    : entitlements.cycle
      ? "L’intégral ouvre la strate entière et la Bibliothèque approfondie."
      : "79 € • Corpus vivant actuellement ouvert.";

  return (
    <div className="lumen-page library-page" id="top">
      <div className="lumen-shell pb-20">
        <header className="lumen-header library-header fade-rise" data-testid="library-header">
          <Link className="lumen-brand" to="/" data-testid="library-brand-link">
            <BrandMark wrapperClassName="lumen-sigil" testId="library-brand-sigil" imageTestId="library-brand-sigil-image" />
            <span data-testid="library-brand-name">{site.brand_name}</span>
          </Link>

          <div className="flex flex-wrap items-center gap-3" data-testid="library-header-actions">
            <span className="lumen-chip library-route-chip" data-testid="library-route-chip">
              /bibliotheque
            </span>
          </div>
        </header>

        <main className="library-page-main space-y-0" data-testid="library-main">
          <section className="lumen-scroll-stage stage-manifest library-vestibule-stage fade-rise delay-1" data-testid="library-vestibule-section">
            <div className="lumen-frame value-major library-vestibule-shell">
              <div className="space-y-5" data-testid="library-vestibule-copy">
                <p className="lumen-kicker" data-testid="library-vestibule-kicker">
                  Vestibule
                </p>
                <h1 className="lumen-title library-page-title text-5xl sm:text-6xl lg:text-7xl" data-testid="library-page-title">
                  {site.library_eyebrow}
                </h1>
              </div>

              <div className="library-route-mark" data-testid="library-route-mark">
                /bibliotheque
              </div>
            </div>
          </section>

          <section className="lumen-scroll-stage stage-fragment library-law-stage fade-rise delay-2" data-testid="library-law-section">
            <div className="space-y-4">
              <div className="lumen-section-heading" data-testid="library-law-heading-row">
                <h2 className="lumen-title text-3xl sm:text-4xl" data-testid="library-law-title">
                  Loi du lieu
                </h2>
                <span className="lumen-plus" data-testid="library-law-plus">
                  +
                </span>
              </div>
              <Card className="lumen-card value-fragment library-law-card" data-testid="library-law-card">
                <CardContent className="space-y-4 p-8">
                  <p className="text-xl leading-relaxed text-[var(--lumen-ivory)]" data-testid="library-law-line-1">
                    {libraryCopy.law[0]}
                  </p>
                  <p className="lumen-copy" data-testid="library-law-line-2">
                    {libraryCopy.law[1]}
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>

          <section className="lumen-scroll-stage stage-doctrine library-cartography-stage fade-rise delay-3" data-testid="library-cartography-section">
            <div className="space-y-4">
              <div className="lumen-section-heading" data-testid="library-cartography-heading-row">
                <h2 className="lumen-title text-3xl sm:text-4xl" data-testid="library-cartography-title">
                  Cartographie
                </h2>
                <span className="lumen-plus" data-testid="library-cartography-plus">
                  +
                </span>
              </div>
              <p className="lumen-copy library-support-line" data-testid="library-cartography-line">
                {libraryCopy.cartography}
              </p>
            </div>

            <div className="library-territory-grid" data-testid="library-territory-grid">
              {site.library_territories.map((territory) => (
                <div className="lumen-card value-doctrine library-territory-tile" key={territory.id} data-testid={`library-territory-tile-${territory.id}`}>
                  <div className="library-territory-inner">
                    <span className="lumen-panel-kicker" data-testid={`library-territory-label-${territory.id}`}>
                      {territory.title}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="lumen-scroll-stage stage-fragment library-depths-stage fade-rise delay-3" data-testid="library-depths-section">
            <div className="space-y-4">
              <div className="lumen-section-heading" data-testid="library-depths-heading-row">
                <h2 className="lumen-title text-3xl sm:text-4xl" data-testid="library-depths-title">
                  Profondeurs
                </h2>
                <span className="lumen-plus" data-testid="library-depths-plus">
                  +
                </span>
              </div>
              <p className="lumen-copy library-support-line" data-testid="library-depths-line">
                {libraryCopy.depths}
              </p>
            </div>

            <div className="library-depth-grid" data-testid="library-depth-grid">
              {depthZones.map((zone) => (
                <Card className="lumen-card value-fragment library-depth-card" key={zone.id} data-testid={`library-depth-card-${zone.id}`}>
                  <CardContent className="space-y-5 p-8">
                    <h3 className="lumen-card-title text-2xl" data-testid={`library-depth-title-${zone.id}`}>
                      {zone.title}
                    </h3>
                    {zone.items.length > 0 ? (
                      <div className="library-depth-chip-row" data-testid={`library-depth-items-${zone.id}`}>
                        {zone.items.map((item) => (
                          <span className="lumen-chip library-depth-chip" key={item} data-testid={`library-depth-item-${zone.id}-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                            {item}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="library-depth-void" data-testid={`library-depth-void-${zone.id}`} />
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <section className="lumen-scroll-stage stage-fragment library-constellation-stage fade-rise delay-4" data-testid="library-constellation-section">
            <div className="space-y-4">
              <div className="lumen-section-heading" data-testid="library-constellation-heading-row">
                <h2 className="lumen-title text-3xl sm:text-4xl" data-testid="library-constellation-title">
                  Constellation
                </h2>
                <span className="lumen-plus" data-testid="library-constellation-plus">
                  +
                </span>
              </div>
            </div>

            <div className="library-constellation-grid" data-testid="library-constellation-grid">
              {figureConstellation.map((figure) => (
                <Card className={`lumen-card value-fragment library-figure-card library-figure-card--${figure.variant}`} key={figure.id} data-testid={`library-figure-card-${figure.id}`}>
                  <CardContent className="space-y-6 p-8">
                    <div className="space-y-4" data-testid={`library-figure-copy-${figure.id}`}>
                      <p className="lumen-panel-kicker" data-testid={`library-figure-role-${figure.id}`}>
                        {figure.role}
                      </p>
                      <h3 className="lumen-card-title text-3xl" data-testid={`library-figure-name-${figure.id}`}>
                        {figure.name}
                      </h3>
                      <div className="space-y-2" data-testid={`library-figure-axes-${figure.id}`}>
                        <p className="lumen-copy" data-testid={`library-figure-territory-${figure.id}`}>
                          {figure.territory}
                        </p>
                        <p className="lumen-footnote" data-testid={`library-figure-lineage-${figure.id}`}>
                          {figure.lineage}
                        </p>
                      </div>
                    </div>

                    <div className="library-figure-links" data-testid={`library-figure-links-${figure.id}`}>
                      {figure.lessons.map((lesson) => (
                        <Link className="lumen-nav-link library-figure-link" key={lesson.slug} to={`/lecons/${lesson.slug}`} data-testid={`library-figure-lesson-link-${figure.id}-${lesson.slug}`}>
                          {lesson.title}
                        </Link>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <section className="lumen-scroll-stage stage-fragment library-traversal-stage fade-rise delay-4" data-testid="library-traversal-section">
            <div className="space-y-4">
              <div className="lumen-section-heading" data-testid="library-traversal-heading-row">
                <h2 className="lumen-title text-3xl sm:text-4xl" data-testid="library-traversal-title">
                  Traversée
                </h2>
                <span className="lumen-plus" data-testid="library-traversal-plus">
                  +
                </span>
              </div>
              <div className="lumen-frame value-fragment library-traversal-shell" data-testid="library-traversal-shell">
                <p className="text-xl leading-relaxed text-[var(--lumen-ivory)]" data-testid="library-traversal-line">
                  {traversalTitle}
                </p>
                <p className="lumen-copy mt-4" data-testid="library-traversal-copy">
                  {traversalCopy}
                </p>
                <div className="lesson-preview-gate-actions mt-6" data-testid="library-traversal-actions">
                  <Button asChild className="lumen-gold-button" data-testid="library-traversal-integral-button">
                    <Link to="/entrer">
                      LUMEN Intégral — 79 €
                      <ArrowRight />
                    </Link>
                  </Button>
                  <Button asChild className="lumen-outline-button" data-testid="library-traversal-cycle-button" variant="outline">
                    <Link to="/entrer">
                      Entrer dans LUMEN — 29 €
                      <ArrowRight />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </section>

          <section className="lumen-scroll-stage stage-fragment library-secondary-stage fade-rise delay-4" data-testid="library-secondary-section">
            <div className="space-y-4">
              <div className="lumen-section-heading" data-testid="library-secondary-heading-row">
                <h2 className="lumen-title text-3xl sm:text-4xl" data-testid="library-secondary-title">
                  Lien secondaire
                </h2>
                <span className="lumen-plus" data-testid="library-secondary-plus">
                  +
                </span>
              </div>

              <div className="library-secondary-links" data-testid="library-secondary-links">
                <Link className="lumen-nav-link library-secondary-link" to="/" data-testid="library-secondary-home-link">
                  Sanctuaire
                </Link>
                <Link className="lumen-nav-link library-secondary-link" to="/parcours" data-testid="library-secondary-journey-link">
                  Parcours
                </Link>
                {firstLesson ? (
                  <Link className="lumen-nav-link library-secondary-link" to={`/lecons/${firstLesson.slug}`} data-testid="library-secondary-lesson-link">
                    Première chambre
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                ) : null}
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}